import React, { useEffect, useState } from 'react';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import { useRecoilValue, useRecoilState } from 'recoil';
import {
  Button,
  Spin
} from 'antd';
import {
  createNewLeadAtom,
  myLeadsHeaderAtom,
  reloadLeadsAtom,
  userAtom
} from '../../../../atoms/Atoms';
import MainPage from '../../../shared-components/main-page/MainPage';
import { H1 } from '../../../shared-components/typography/Title';
import {
  thousand,
} from '../../../../functions';
import CreateNewLeadModal from './shared-components/CreateNewLeadModal';
import Card from '../../../shared-components/container/Card';
import LeadsPending from './shared-components/LeadsPending';
import LeadsOpen from './shared-components/LeadsOpen';
import LeadsClosed from './shared-components/LeadsClosed';
import LeadsLost from './shared-components/LeadsLost';
import SetterCreateNewLeadModal from './shared-components/SetterCreateNewLeadModal';
import api from '../../../../api/api';

function SetterLeads({ includeBack }) {
  const { t } = useTranslation();
  const myLeadsHeader = useRecoilValue(myLeadsHeaderAtom);
  const [createNewLead, setCreateNewLead] = useRecoilState(createNewLeadAtom);
  const reloadLeads = useRecoilValue(reloadLeadsAtom);
  const [selectedTab, setSelectedTab] = useState(1);
  const [loadingTargetData, setLoadingTargetData] = useState(null);
  const [targetData, setTargetData] = useState(null);
  const user = useRecoilValue(userAtom);

  function getTargetData() {
    setLoadingTargetData(true);
    api.get('leads/targets/me?type=1')
    .then((response) => {
      setLoadingTargetData(false);
      if (response.data.success) {
        setTargetData(response.data.data);
      }
    })
    .catch((error) => {
      setLoadingTargetData(false);
    })
  }

  useEffect(() => {
    if (reloadLeads) {
      getTargetData();
    }
  }, [reloadLeads]);

  useEffect(() => {
    getTargetData();
  }, []);

  return (
    <MainPage includeBack requiresSetter>
      <div className="flex mt-4">
        <H1 text={t('my_leads')} />
        <Button
          type="primary"
          className="ml-auto"
          onClick={() => setCreateNewLead({
            ...createNewLead,
            open: true
          })}
        >
          {t('create_new')}
        </Button>
      </div>
      <div className="grid gap-4 mt-4 grid-cols-1 md:grid-cols-2">
        <div className="col-span-1">
          <Spin spinning={loadingTargetData}>
            <Card
              className="w-full rounded-sm"
              textPosition="center"
              title={t('weekly_target')}
              description={`${thousand(targetData?.setterWeeklyLeads)}/${thousand(user?.setterWeeklyLeadsTarget)}`}
              descriptionSize="text-2xl"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4">
                <div className="col-span-1">
                  <p className="font-bold">{t('not_held')}</p>
                  <p>{thousand(targetData?.setterNotHeldWeeklyLeads)}</p>
                </div>
                <div className="col-span-1">
                  <p className="font-bold">{t('held')}</p>
                  <p>{thousand(targetData?.setterHeldWeeklyLeads)}</p>
                </div>
                <div className="col-span-1">
                  <p className="font-bold">{t('revisit')}</p>
                  <p>{thousand(targetData?.setterRevisitWeeklyLeads)}</p>
                </div>
                <div className="col-span-1">
                  <p className="font-bold">{t('failed_credit')}</p>
                  <p>{thousand(targetData?.setterFailedCreditWeeklyLeads)}</p>
                </div>
              </div>
            </Card>
          </Spin>
        </div>
        <div className="col-span-1">
          <Spin spinning={loadingTargetData}>
            <Card
              className="w-full rounded-sm"
              textPosition="center"
              title={t('monthly_target')}
              description={`${thousand(targetData?.setterMonthlyLeads)}/${thousand(user?.setterMonthlyLeadsTarget)}`}
              descriptionSize="text-2xl"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4">
                <div className="col-span-1">
                  <p className="font-bold">{t('not_held')}</p>
                  <p>{thousand(targetData?.setterNotHeldMonthlyLeads)}</p>
                </div>
                <div className="col-span-1">
                  <p className="font-bold">{t('held')}</p>
                  <p>{thousand(targetData?.setterHeldMonthlyLeads)}</p>
                </div>
                <div className="col-span-1">
                  <p className="font-bold">{t('revisit')}</p>
                  <p>{thousand(targetData?.setterRevisitMonthlyLeads)}</p>
                </div>
                <div className="col-span-1">
                  <p className="font-bold">{t('failed_credit')}</p>
                  <p>{thousand(targetData?.setterFailedCreditMonthlyLeads)}</p>
                </div>
              </div>
            </Card>
          </Spin>
        </div>
      </div>
      <div className="grid gap-4 mt-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-4">
        <Spin spinning={myLeadsHeader?.loading}>
          <button
            aria-label={t('leads')}
            type="button"
            className={`col-span-1 w-full border-4 rounded-sm ${selectedTab === 0 ? 'border-primaryColor' : 'border-transparent'}`}
            onClick={() => setSelectedTab(0)}
          >
            <Card
              title={t('leads')}
              description={thousand((myLeadsHeader?.totalPendingAssigned + myLeadsHeader?.totalPendingNoAppointment + myLeadsHeader?.totalNotAssigned))}
            />
          </button>
        </Spin>
        <Spin spinning={myLeadsHeader?.loading}>
          <button
            aria-label={t('deals')}
            type="button"
            className={`col-span-1 w-full border-4 rounded-sm ${selectedTab === 1 ? 'border-primaryColor' : 'border-transparent'}`}
            onClick={() => setSelectedTab(1)}
          >
            <Card
              title={t('deals')}
              description={thousand((myLeadsHeader?.totalAssigned + myLeadsHeader?.totalRevisit + myLeadsHeader?.totalFailedCredit))}
            />
          </button>
        </Spin>
        <Spin spinning={myLeadsHeader?.loading}>
          <button
            aria-label={t('installs')}
            type="button"
            className={`col-span-1 w-full border-4 rounded-sm ${selectedTab === 2 ? 'border-primaryColor' : 'border-transparent'}`}
            onClick={() => setSelectedTab(2)}
          >
            <Card
              title={t('installs')}
              description={thousand((myLeadsHeader?.totalInstalled + myLeadsHeader?.totalActivated))}
            />
          </button>
        </Spin>
        <Spin spinning={myLeadsHeader?.loading}>
          <button
            aria-label={t('lost')}
            type="button"
            className={`col-span-1 w-full border-4 rounded-sm ${selectedTab === 3 ? 'border-primaryColor' : 'border-transparent'}`}
            onClick={() => setSelectedTab(3)}
          >
            <Card
              title={t('lost')}
              description={thousand(myLeadsHeader?.totalLost)}             
            />
          </button>
        </Spin>
      </div>
      {selectedTab === 0 && (
        <LeadsPending isSetter className="mt-2" />
      )}
      {selectedTab === 1 && (
        <LeadsOpen isSetter className="mt-2" />
      )}
      {selectedTab === 2 && (
        <LeadsClosed isSetter className="mt-2" />
      )}
      {selectedTab === 3 && (
        <LeadsLost isSetter className="mt-2" />
      )}
      <SetterCreateNewLeadModal />
    </MainPage>
  );
}

export default SetterLeads;
